import { ifString } from 'app/utils/text';
import { RequestOptions, IAuthorizationToken } from 'app/utils/request';
import createErrorType from 'app/utils/createErrorType';
import { List, Map } from 'immutable';
import { Cause } from 'app/constants/schemas/Cause';

export type ResponseErrorArgs = Partial<Pick<
  ResponseError,
  'data' |
  'method' |
  'causes' |
  'detail' |
  'meta' |
  'readyState' |
  'requestHeaders' |
  'response' |
  'responseURL' |
  'status' |
  'statusText' |
  'tokenExpiresIn' |
  'tokenScope' |
  'tokenType' |
  'url' |
  'withCredentials'
>>;
// Default arguments for ResponseError types.
const defaults: ResponseErrorArgs = Object.freeze({
  data: undefined,
  message: '',
  method: undefined,
  requestHeaders: undefined,
  response: undefined,
  status: undefined,
  statusText: undefined,
  url: undefined,
});

export class ResponseError extends createErrorType('ResponseError') {
  public causes?: List<Cause>;
  public readonly detail?: string;
  public meta?: Map<string, unknown>;
  public code?: string;
  public readonly data: RequestOptions['data'];
  public readonly method?: RequestOptions['method'];
  public readonly readyState?: XMLHttpRequest['readyState'];
  public readonly requestHeaders: RequestOptions['requestHeaders'];
  public readonly response: XMLHttpRequest['response'];
  public readonly responseURL?: XMLHttpRequest['responseURL'];
  public readonly status?: XMLHttpRequest['status'];
  public readonly statusText?: XMLHttpRequest['statusText'];
  public readonly tokenExpiresIn?: IAuthorizationToken['expires_in'];
  public readonly tokenScope?: IAuthorizationToken['scope'];
  public readonly tokenType?: IAuthorizationToken['token_type'];
  public readonly url?: RequestOptions['url'];
  public readonly withCredentials?: XMLHttpRequest['withCredentials'];
  public constructor(args: ResponseErrorArgs = {}) {
    super();

    // Assign all the arguments passed in.
    Object.assign(this, defaults, args);

    const { response } = this;

    // See if there is an error message that can be set.
    this.message =
      ifString(response && response.error && response.error.error) ||
      ifString(response && response.error) ||
      ifString(response) ||
      this.statusText ||
      '(No response)';
  }

  public toJSON() {
    const convertHash: {[index: string]: unknown} = {};
    Object.keys(defaults).forEach(key => {
      if (key in this) {
        convertHash[key] = this[key as keyof this];
      }
    });
    return convertHash;
  }
}

export class AbortedError extends ResponseError {
  public name: 'AbortedError';
  public constructor(values: ResponseErrorArgs) {
    super({ ...values });
    this.name = 'AbortedError';
  }
}

export class TimeoutError extends ResponseError {
  public name: 'Timeout';
  public constructor(values: ResponseErrorArgs) {
    super({ ...values });
    this.name = 'Timeout';
  }
}

export class NetworkError extends ResponseError {
  public name: 'NetworkError';
  public constructor(values: ResponseErrorArgs) {
    super({ ...values });
    this.name = 'NetworkError';
  }
}
